/**
 * Time on the audit-log screen: transported as UTC, shown in whichever clock
 * the reader has selected, with that clock always named (f1.5, BR-F1.5-15).
 *
 * An Admin correlating an entry with a phone call reasons in their own clock,
 * so LOCAL is the default -- but an investigation that spans zones, or that is
 * written up against server logs, reasons in UTC, so the screen offers the
 * choice as a filter-bar flag and every timestamp on the screen follows it
 * together: the list column and the expanded entry can never disagree.
 *
 * An UNLABELLED time invites off-by-hours conclusions either way, so the zone
 * is always on screen next to the timestamps.
 *
 * The date RANGE inputs are a separate matter and stay in the reader's local
 * calendar whatever the display flag says: they are a calendar-picking
 * gesture, and silently re-interpreting "the 20th" as a UTC day when a display
 * toggle flips would move the result set without the reader asking.
 */

/** A calendar day in the reader's zone, as `<input type="date">` spells it. */
export type LocalDay = string;

/** Which clock every timestamp on the screen is rendered in. */
export type TimeZoneMode = 'local' | 'utc';

const pad = (value: number, width = 2) => String(value).padStart(width, '0');

function parseDay(day: LocalDay): [number, number, number] {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) throw new Error(`Not a calendar day: ${day}`);
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

/** Whether a string is a valid `YYYY-MM-DD` day. */
export function isLocalDay(value: string): value is LocalDay {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = parseDay(value);
  const probe = new Date(year, month - 1, day);
  return (
    probe.getFullYear() === year && probe.getMonth() === month - 1 && probe.getDate() === day
  );
}

/** A `Date` as a local calendar day. */
export function toLocalDay(moment: Date): LocalDay {
  return `${moment.getFullYear()}-${pad(moment.getMonth() + 1)}-${pad(moment.getDate())}`;
}

/** Today, in the reader's zone. */
export function todayLocal(now: Date = new Date()): LocalDay {
  return toLocalDay(now);
}

/**
 * A day moved by whole calendar days.
 *
 * Built from calendar parts rather than by adding 24 hours, so a daylight-
 * saving change inside the span cannot land the result on the wrong day.
 */
export function addDays(day: LocalDay, days: number): LocalDay {
  const [year, month, date] = parseDay(day);
  return toLocalDay(new Date(year, month - 1, date + days));
}

/** The first instant of a local day -- local midnight, as an absolute time. */
export function startOfLocalDay(day: LocalDay): Date {
  const [year, month, date] = parseDay(day);
  return new Date(year, month - 1, date, 0, 0, 0, 0);
}

/**
 * The half-open instant range covering local days `from` … `to` INCLUSIVE.
 *
 * The server's range is half-open (`occurred_to` exclusive) so adjacent ranges
 * tile without showing a boundary entry twice. A reader picking "to the 20th"
 * means the whole of the 20th, so the upper bound sent is the start of the
 * 21st.
 */
export function instantRange(from: LocalDay, to: LocalDay): { from: string; to: string } {
  return {
    from: startOfLocalDay(from).toISOString(),
    to: startOfLocalDay(addDays(to, 1)).toISOString(),
  };
}

/**
 * The short name of the reader's zone, e.g. `CEST`, `EDT` or `GMT+2`.
 *
 * Taken for the given instant, because the abbreviation itself changes across
 * a daylight-saving boundary.
 */
export function zoneLabel(at: Date = new Date()): string {
  try {
    const part = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
      .formatToParts(at)
      .find((entry) => entry.type === 'timeZoneName');
    if (part?.value) return part.value;
  } catch {
    /* Fall through to an offset computed by hand. */
  }
  const offset = -at.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const magnitude = Math.abs(offset);
  return `UTC${sign}${pad(Math.floor(magnitude / 60))}:${pad(magnitude % 60)}`;
}

/** `YYYY-MM-DD HH:mm:ss` in the reader's zone. The list's timestamp format. */
export function formatLocalTimestamp(iso: string): string {
  const moment = new Date(iso);
  if (Number.isNaN(moment.getTime())) return iso;
  return (
    `${toLocalDay(moment)} ${pad(moment.getHours())}:` +
    `${pad(moment.getMinutes())}:${pad(moment.getSeconds())}`
  );
}

/**
 * The same format, in UTC -- the clock the entry was actually stored in.
 *
 * Built from the `getUTC*` parts rather than by slicing the ISO string,
 * because the server's instant may carry milliseconds or an offset other than
 * `Z`, and a slice would render either one wrong.
 */
export function formatUtcTimestamp(iso: string): string {
  const moment = new Date(iso);
  if (Number.isNaN(moment.getTime())) return iso;
  return (
    `${moment.getUTCFullYear()}-${pad(moment.getUTCMonth() + 1)}-${pad(moment.getUTCDate())} ` +
    `${pad(moment.getUTCHours())}:${pad(moment.getUTCMinutes())}:${pad(moment.getUTCSeconds())}`
  );
}

/** One timestamp, in whichever clock the reader selected. */
export function formatTimestamp(iso: string, mode: TimeZoneMode): string {
  return mode === 'utc' ? formatUtcTimestamp(iso) : formatLocalTimestamp(iso);
}

/** The name to print beside those timestamps, so the clock is never implied. */
export function zoneLabelFor(mode: TimeZoneMode, at: Date = new Date()): string {
  return mode === 'utc' ? 'UTC' : zoneLabel(at);
}

/** A short human range for the info line, e.g. `13 Sep 10:04 - 20 Sep 09:41`. */
export function formatLocalShort(iso: string): string {
  const moment = new Date(iso);
  if (Number.isNaN(moment.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(moment);
}
